import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { API } from '../config/api';
import { safeJson } from '../utils/safeJson';

import { 
  Palette, MapPin, ExternalLink, Image, Users, 
  Loader2, Share2, UserPlus, UserMinus, MessageSquare,
  Phone, X
} from 'lucide-react';


export default function PublicDesignerPage() {
  const { handle } = useParams();
  const navigate = useNavigate();
  const { user, token, isAuthenticated } = useAuth();
  
  const [designer, setDesigner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFollowing, setIsFollowing] = useState(false);
  const [showInquiryForm, setShowInquiryForm] = useState(false);
  const [submittingInquiry, setSubmittingInquiry] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  
  const [inquiryForm, setInquiryForm] = useState({
    name: '',
    email: '',
    handle: '',
    project_type: '',
    budget_range: '',
    deadline: '',
    message: ''
  });

  useEffect(() => {
    fetchDesigner();
  }, [handle]);

  useEffect(() => {
    if (user) {
      setInquiryForm(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || '',
        handle: user.handle || ''
      }));
    }
  }, [user]);

  const fetchDesigner = async () => {
    try {
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const res = await fetch(`${API}/designers/${handle}`, { headers });
      
      if (!res.ok) {
        if (res.status === 404) {
          navigate('/404');
          return;
        }
        throw new Error('Failed to fetch designer');
      }
      
      const data = await res.json();
      setDesigner(data);
      
      // Check if following
      if (isAuthenticated && user) {
        const followRes = await fetch(`${API}/users/${data.user_id}/is-following`, { headers });
        if (followRes.ok) {
          const followData = await followRes.json();
          setIsFollowing(followData.is_following);
        }
      }
    } catch (err) {
      console.error('Error:', err);
      toast.error('Failed to load designer profile');
    } finally {
      setLoading(false);
    }
  };

  const toggleFollow = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    try {
      const endpoint = isFollowing 
        ? `${API}/users/${designer.user_id}/unfollow`
        : `${API}/users/${designer.user_id}/follow`;
      
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        setIsFollowing(!isFollowing);
        setDesigner(prev => ({
          ...prev,
          follower_count: prev.follower_count + (isFollowing ? -1 : 1)
        }));
      }
    } catch (err) {
      toast.error('Failed to update follow');
    }
  };

  const submitInquiry = async (e) => {
    e.preventDefault();
    setSubmittingInquiry(true);
    
    try {
      const res = await fetch(`${API}/inquiries/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          creator_id: designer.user_id,
          creator_type: 'graphic_designer',
          ...inquiryForm
        })
      });
      
      if (res.ok) {
        toast.success('Inquiry sent! The designer will get back to you.');
        setShowInquiryForm(false);
        setInquiryForm(prev => ({ ...prev, project_type: '', budget_range: '', deadline: '', message: '' }));
      } else {
        throw new Error('Failed to send inquiry');
      }
    } catch (err) {
      toast.error('Failed to send inquiry. Please try again.');
    } finally {
      setSubmittingInquiry(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!designer) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Designer not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4" data-testid="public-designer-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <Card className="glass-card border-pink-500/20 p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-6">
            {/* Avatar */}
            <div className="flex-shrink-0">
              {designer.picture ? (
                <img
                  src={designer.picture}
                  alt={designer.name}
                  className="w-32 h-32 rounded-full object-cover border-4 border-pink-500/30"
                />
              ) : (
                <div className="w-32 h-32 rounded-full bg-pink-500/20 flex items-center justify-center border-4 border-pink-500/30">
                  <Palette className="w-16 h-16 text-pink-400" />
                </div>
              )}
            </div>
            
            {/* Info */}
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <h1 className="font-heading text-3xl font-bold">{designer.name}</h1>
                {designer.is_verified && (
                  <Badge className="bg-pink-500/20 text-pink-400">Verified</Badge>
                )}
                {designer.phone_verified && (
                  <Badge className="bg-green-500/20 text-green-400">
                    <Phone className="w-3 h-3 mr-1" /> Verified
                  </Badge>
                )}
              </div>
              
              <p className="text-muted-foreground mb-2">{designer.handle ? `@${designer.handle}` : ""}</p>
              
              {designer.profile?.location?.city && (
                <p className="text-sm text-muted-foreground flex items-center gap-1 mb-3">
                  <MapPin className="w-4 h-4" />
                  {designer.profile.location.city}{designer.profile.location.state ? `, ${designer.profile.location.state}` : ''}
                </p>
              )}
              
              {designer.profile?.specialties?.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {designer.profile.specialties.map((spec, idx) => (
                    <Badge key={idx} variant="outline">{spec}</Badge>
                  ))}
                </div>
              )}
              
              <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                <span><strong>{designer.follower_count || 0}</strong> followers</span>
                <span><strong>{designer.portfolio?.length || 0}</strong> works</span>
              </div>
              
              {designer.profile?.bio && (
                <p className="text-sm mb-4">{designer.profile.bio}</p>
              )}
              
              {/* Actions */}
              <div className="flex gap-2">
                <Button
                  onClick={toggleFollow}
                  variant={isFollowing ? 'outline' : 'default'}
                  className={!isFollowing ? 'bg-pink-600 hover:bg-pink-700' : ''}
                >
                  {isFollowing ? <><UserMinus className="w-4 h-4 mr-2" /> Unfollow</> : <><UserPlus className="w-4 h-4 mr-2" /> Follow</>}
                </Button>
                <Button variant="outline" onClick={() => setShowInquiryForm(true)}>
                  <MessageSquare className="w-4 h-4 mr-2" /> Request Work
                </Button>
                <Button variant="ghost" size="icon">
                  <Share2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </Card>

        {/* Inquiry Form Modal */}
        {showInquiryForm && (
          <div className="fixed inset-0 bg-background/50 flex items-center justify-center z-50 p-4">
            <Card className="glass-card border-pink-500/20 p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-xl font-bold">Request Work / Inquiry</h3>
                <Button variant="ghost" size="icon" onClick={() => setShowInquiryForm(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <form onSubmit={submitInquiry} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Your Name</Label>
                    <Input
                      value={inquiryForm.name}
                      onChange={(e) => setInquiryForm({ ...inquiryForm, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={inquiryForm.email}
                      onChange={(e) => setInquiryForm({ ...inquiryForm, email: e.target.value })}
                      required
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Project Type</Label>
                  <Input
                    placeholder="e.g., Cover Art, Logo, Motion Graphics"
                    value={inquiryForm.project_type}
                    onChange={(e) => setInquiryForm({ ...inquiryForm, project_type: e.target.value })}
                    required
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Budget Range (optional)</Label>
                    <Input
                      placeholder="e.g., $50-200"
                      value={inquiryForm.budget_range}
                      onChange={(e) => setInquiryForm({ ...inquiryForm, budget_range: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Deadline (optional)</Label>
                    <Input
                      type="date"
                      value={inquiryForm.deadline}
                      onChange={(e) => setInquiryForm({ ...inquiryForm, deadline: e.target.value })}
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Message</Label>
                  <Textarea
                    placeholder="Tell the designer about your project..."
                    value={inquiryForm.message}
                    onChange={(e) => setInquiryForm({ ...inquiryForm, message: e.target.value })}
                    rows={4}
                    required
                  />
                </div>
                
                <Button type="submit" className="w-full bg-pink-600 hover:bg-pink-700" disabled={submittingInquiry}>
                  {submittingInquiry ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MessageSquare className="w-4 h-4 mr-2" />}
                  Send Inquiry
                </Button>
              </form>
            </Card>
          </div>
        )}

        {/* Pricing Tiers */}
        {designer.profile?.pricing_tiers?.length > 0 && (
          <Card className="glass-card border-pink-500/20 p-6 mb-6">
            <h3 className="font-heading text-lg font-bold mb-4">Services & Pricing</h3>
            <div className="grid md:grid-cols-3 gap-4">
              {designer.profile.pricing_tiers.map((tier, idx) => (
                <div key={idx} className="p-4 rounded-lg bg-muted/30 text-center">
                  <h4 className="font-bold mb-1">{tier.name}</h4>
                  <p className="text-2xl font-bold text-pink-400 mb-2">{tier.price}</p>
                  <p className="text-xs text-muted-foreground">{tier.description}</p>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Portfolio */}
        <Card className="glass-card border-pink-500/20 p-6">
          <h3 className="font-heading text-lg font-bold mb-4">Portfolio</h3>
          
          {designer.portfolio?.length === 0 ? (
            <div className="text-center py-12">
              <Image className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No portfolio items yet</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {designer.portfolio.map((item) => (
                <div 
                  key={item.item_id} 
                  className="aspect-square rounded-lg overflow-hidden cursor-pointer hover:ring-2 hover:ring-pink-500/50 transition-all"
                  onClick={() => setSelectedImage(item)}
                >
                  {item.media_url ? (
                    item.media_type === 'video' ? (
                      <video 
                        src={item.media_url} 
                        className="w-full h-full object-cover"
                        muted
                      />
                    ) : (
                      <img 
                        src={item.media_url} 
                        alt={item.title} 
                        className="w-full h-full object-cover"
                      />
                    )
                  ) : (
                    <div className="w-full h-full bg-pink-500/10 flex items-center justify-center">
                      <Image className="w-12 h-12 text-pink-400" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Lightbox */}
        {selectedImage && (
          <div 
            className="fixed inset-0 bg-background/90 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedImage(null)}
          >
            <div className="max-w-4xl max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
              {selectedImage.media_type === 'video' ? (
                <video 
                  src={selectedImage.media_url} 
                  className="max-w-full max-h-[80vh] rounded-lg"
                  controls
                  autoPlay
                />
              ) : (
                <img 
                  src={selectedImage.media_url} 
                  alt={selectedImage.title}
                  className="max-w-full max-h-[80vh] rounded-lg"
                />
              )}
              {selectedImage.title && (
                <p className="text-center mt-4 font-medium">{selectedImage.title}</p>
              )}
              {selectedImage.description && (
                <p className="text-center text-sm text-muted-foreground">{selectedImage.description}</p>
              )}
            </div>
            <Button 
              variant="ghost" 
              size="icon" 
              className="absolute top-4 right-4"
              onClick={() => setSelectedImage(null)}
            >
              <X className="w-6 h-6" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
